/** Reuse the rendered table cells for a non-interactive drag image. */
/* eslint-disable functional/immutable-data -- DOM snapshot styling is an imperative browser API, not application state. */

/**
 * Freeze column geometry while removing interactive identities from the copy.
 * @param source - Live row registered with the sortable controller.
 * @returns Inert table containing a frozen copy of the source row.
 */
export const createTableDragPreview = (
  source: HTMLElement
): HTMLTableElement => {
  const table = document.createElement("table");
  const body = document.createElement("tbody");
  const row = source.cloneNode(true) as HTMLElement;
  table.className = "border-collapse bg-surface-base";
  table.style.width = `${source.getBoundingClientRect().width}px`;
  table.setAttribute("aria-hidden", "true");
  row.removeAttribute("style");
  row.className = "bg-surface-base";
  for (const element of [
    row,
    ...Array.from(row.querySelectorAll<HTMLElement>("*")),
  ]) {
    element.removeAttribute("id");
    element.removeAttribute("data-testid");
    element.removeAttribute("aria-describedby");
    element.setAttribute("tabindex", "-1");
  }
  Array.from(row.children).forEach((cell, index) => {
    const copy = cell as HTMLElement;
    const width = source.children[index].getBoundingClientRect().width;
    copy.style.width = `${width}px`;
    copy.style.minWidth = `${width}px`;
    copy.style.maxWidth = `${width}px`;
    copy.style.position = "static";
    copy.style.boxShadow = "none";
  });
  body.appendChild(row);
  table.appendChild(body);
  return table;
};

/**
 * Show the source-bound post-drop highlight and return its cleanup.
 * @param row - Moved live row.
 * @returns Cleanup that restores the original cell appearance.
 */
export const highlightDroppedRow = (row: HTMLElement) => {
  const cells = Array.from(row.children);
  row.setAttribute("data-reorder-dropped", "true");
  cells.forEach(cell => cell.classList.add("!bg-surface-info-subtle"));
  return () => {
    row.removeAttribute("data-reorder-dropped");
    cells.forEach(cell => cell.classList.remove("!bg-surface-info-subtle"));
  };
};
/* eslint-enable functional/immutable-data -- End imperative DOM snapshot scope. */
